import React, { useState, useEffect } from 'react';
import PublicNav from '../components/PublicNav';
import { BackToHome } from '../components/BackToHome';
import ComplianceRequestDialog from '../components/ComplianceRequestDialog';
import { Button } from '../components/ui/button';
import { complianceAPI } from '../lib/api';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '2.25rem' }}>
    <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.75rem' }}>{title}</h2>
    <div style={{ fontSize: '0.9375rem', color: '#475569', lineHeight: 1.8 }}>{children}</div>
  </div>
);

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : null);

export default function AITransparencyPage() {
  const [dialog, setDialog] = useState(null); // 'aedt_alternative' | 'ada_accommodation'
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    complianceAPI.getPublicBiasAudit()
      .then((res) => setAudit(res.data.audit))
      .catch(() => {});
  }, []);

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }} data-testid="ai-transparency-page">
      <PublicNav />
      <BackToHome testid="back-to-home-top" />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '3rem 2rem 4rem' }}>
        <span style={{ display: 'inline-block', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#0076A3', background: 'rgba(30,104,255,0.08)', border: '1px solid rgba(30,104,255,0.15)', padding: '0.25rem 0.625rem', borderRadius: 999, marginBottom: '1rem' }}>
          AI Transparency · NYC Local Law 144
        </span>
        <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2.25rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.75rem' }}>How We Use AI in Hiring</h1>
        <p style={{ fontSize: '1rem', color: '#64748B', lineHeight: 1.8, marginBottom: '2.5rem' }}>
          CHIRE provides tools that may qualify as an <strong>Automated Employment Decision Tool (AEDT)</strong> under New York City Local Law 144. This page explains, in plain language, where automation is used, what data it relies on, and the rights you have as a candidate — including the right to request an alternative process or a disability accommodation.
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '3rem' }}>
          <Button data-testid="open-aedt-alternative" onClick={() => setDialog('aedt_alternative')} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, padding: '0.75rem 1.25rem' }}>
            Request an Alternative Process
          </Button>
          <Button data-testid="open-ada-accommodation" onClick={() => setDialog('ada_accommodation')} variant="outline" style={{ borderRadius: 10, fontWeight: 600, padding: '0.75rem 1.25rem', borderColor: '#0076A3', color: '#0076A3' }}>
            Request an Accommodation
          </Button>
        </div>

        <Section title="Where automation is used">
          <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
            <li><strong>Resume screening &amp; scoring:</strong> AI parses your resume and produces a fit score against the job's required skills to help recruiters prioritize review.</li>
            <li><strong>Skills assessments:</strong> AI may generate questions and assist in scoring your responses against a standardized rubric.</li>
            <li><strong>AI video interviews:</strong> where used, AI assists in structuring questions and summarizing responses.</li>
          </ul>
        </Section>

        <Section title="What the tools assess">
          The tools evaluate job-related qualifications — such as skills, experience, and assessment responses — relative to the requirements of the role. They are configured to focus on competencies relevant to the position.
        </Section>

        <Section title="Human oversight">
          Automated scores are <strong>decision support, not decision makers</strong>. A recruiter reviews candidates and controls every stage of progression. CHIRE does not auto-reject or auto-advance candidates based solely on an AI score.
        </Section>

        <Section title="Data we rely on">
          The tools use the information you provide — your resume, application details, assessment answers, and (where applicable, with your separate consent) video interview responses. We do not use protected characteristics such as race, gender, age, or disability as inputs to scoring.
        </Section>

        <Section title="Bias audits">
          NYC Local Law 144 requires that AEDTs undergo an independent bias audit before use and that a summary of results be made publicly available.
          {audit ? (
            <div data-testid="published-bias-audit" style={{ marginTop: '1rem', padding: '1rem 1.25rem', borderRadius: 12, background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#166534', marginBottom: '0.375rem' }}>
                Latest independent bias audit
              </div>
              <div style={{ fontSize: '0.8125rem', color: '#15803D', lineHeight: 1.7 }}>
                <div><strong>Auditor:</strong> {audit.auditor_name}</div>
                {fmtDate(audit.audit_date) && <div><strong>Audit date:</strong> {fmtDate(audit.audit_date)}</div>}
                {fmtDate(audit.distribution_date) && <div><strong>Distribution date:</strong> {fmtDate(audit.distribution_date)}</div>}
                {audit.summary && <p style={{ margin: '0.5rem 0 0' }}>{audit.summary}</p>}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                {audit.file_url && <a data-testid="bias-audit-pdf-link" href={`${apiBase}${audit.file_url}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3' }}>Download audit summary (PDF)</a>}
                {audit.source_url && <a data-testid="bias-audit-source-link" href={audit.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3' }}>View published summary</a>}
              </div>
            </div>
          ) : (
            <p style={{ marginTop: '0.75rem' }}>Where CHIRE tools are used as an AEDT for a NYC role, the most recent bias-audit summary and distribution-date information are made available by the hiring employer. Contact the hiring organization or our team to obtain the applicable audit summary.</p>
          )}
        </Section>

        <Section title="Your rights">
          <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
            <li>Request an <strong>alternative selection process</strong> instead of the automated tool.</li>
            <li>Request a <strong>reasonable accommodation</strong> under the ADA.</li>
            <li>Request access to or deletion of your data via our <a href="/legal/data-request" style={{ color: '#0076A3', fontWeight: 600 }}>Data Request</a> form (and <a href="/interview-data-request" style={{ color: '#0076A3', fontWeight: 600 }}>interview data</a> form for video interviews).</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>Exercising any of these rights will not negatively affect your candidacy.</p>
        </Section>

        <Section title="Notice &amp; questions">
          Candidates for NYC-based roles are notified at least 10 business days before an AEDT is used, as required by law. For questions about how AI is used in your specific application, contact the hiring organization or see our <a href="/legal/privacy" style={{ color: '#0076A3', fontWeight: 600 }}>Privacy Policy</a>.
        </Section>

        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: '2rem', lineHeight: 1.7 }}>
          This page is provided for transparency and does not constitute legal advice. Specific compliance obligations (including bias-audit publication) rest with the employer using the tools.
        </p>
      </div>

      <ComplianceRequestDialog
        open={!!dialog}
        onOpenChange={() => setDialog(null)}
        category={dialog || 'aedt_alternative'}
        context="ai_transparency_page"
      />
      <BackToHome testid="back-to-home-bottom" style={{ paddingBottom: '2.5rem' }} />
    </div>
  );
}
